<template>
  <div class="product__media" v-if="productMediaGallery.length > 0">
    <div class="slider__container">
      <div class="slider__container-inner">
        <span v-if="actionLabel" class="product__label">{{ actionLabel }}</span>
        <span v-else-if="hasDiscount" class="discount__label">{{ discountPercent }}%</span>
        <swiper ref="gallerySlider" :options="slideOptions">
          <swiper-slide v-for="(image, index) in productMediaGallery" :key="index">
            <div
              class="image__holder"
              :class="{ '_zooming': canHoverZoom && isHoverZooming && index === activeSlide && image.type === 'image' }"
              v-if="image.type === 'image'"
              @mousemove="onHoverZoom"
              @mouseenter="startHoverZoom"
              @mouseleave="stopHoverZoom"
            >
              <a
                :href="zoomMediaUrl(image)"
                class="lightbox"
                :data-index="index"
                data-gallery="product"
              >
                <img
                  :data-src="displayMediaUrl(image)"
                  :data-fallback="originalMediaUrl(image)"
                  width="520"
                  height="520"
                  class="swiper-lazy"
                  :alt="image.label || productData.name"
                  @error="onSizedImageError"
                >
              </a>
              <div
                class="image__zoom"
                v-show="canHoverZoom && isHoverZooming && index === activeSlide"
                :style="zoomStyle(image)"
              ></div>
            </div>
            <span class="video__holder" v-else>
              <span class="product__video">
                <iframe v-if="image.typeVideo === 'vimeo'" :src="`https://player.vimeo.com/video/${image.video}`" width="640" height="360" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
                <iframe v-else width="640" height="360" :src="`https://www.youtube.com/embed/${image.video}`" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
              </span>
              <a
                :href="videoHref(image)"
                class="lightbox"
                :data-index="index"
                data-gallery="product"
                data-type="video"
                tabindex="-1"
                aria-hidden="true"
              >{{ productData.name }}</a>
            </span>
          </swiper-slide>
        </swiper>
        <div class="slider__controls productMediaSlider" v-if="productMediaGallery.length > 1">
          <div class="slider__control prev" slot="button-prev"></div>
          <div class="slider__control next" slot="button-next"></div>
        </div>
      </div>
    </div>

    <div
      class="thumbnail__container slider__container"
      :class="{
        '_overflow-start': thumbOverflowStart,
        '_overflow-end': thumbOverflowEnd
      }"
      v-if="productMediaGallery.length > 1"
    >
      <swiper ref="thumbSlider" :options="thumbOptions">
        <swiper-slide v-for="(image, index) in productMediaGallery" :key="index">
          <span
            @click="onThumbClick(image, index)"
            :class="{ _active: index === activeSlide }"
          >
            <img
              v-if="image.type === 'image'"
              v-lazy="thumbMediaUrl(image)"
              :data-fallback="originalMediaUrl(image)"
              width="80"
              height="80"
              :alt="image.label || productData.name"
              @error="onSizedImageError"
            />
            <span v-else class="thumbnail__video" aria-label="Video">
              <img
                v-if="videoPoster(image)"
                v-lazy="videoPoster(image)"
                width="80"
                height="80"
                :alt="productData.name"
              />
              <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <circle cx="24" cy="24" r="23" />
                <path d="M20 16.2v15.6L33.5 24z" />
              </svg>
            </span>
          </span>
        </swiper-slide>
      </swiper>
      <div class="slider__controls productThumbSlider">
        <div class="slider__control prev" slot="button-prev"></div>
        <div class="slider__control next" slot="button-next"></div>
      </div>
    </div>
  </div>
  <div class="product__media" v-else>
    <span class="no__image">
      <span v-if="actionLabel" class="product__label">{{ actionLabel }}</span>
      <span v-else-if="hasDiscount" class="discount__label">{{ discountPercent }}%</span>
      <img
        v-lazy="defaultImageUrl"
        width="520"
        height="520"
        :alt="productData.name"
      />
    </span>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import GLightbox from "glightbox";
import "glightbox/dist/css/glightbox.css";
import { getProductDiscountPercent, productHasDiscount } from "../../../shared/helpers/discount";

@Component({
  name: "catalog-product-media",
  components: {
    Swiper,
    SwiperSlide
  }
})
export default class CatalogProductMedia extends Vue {
  @Prop() readonly productData!: Record<string, any>;
  protected activeSlide: number = 0;
  private addVideo: boolean = true;
  private lightbox: ReturnType<typeof GLightbox> | null = null;
  private swiperInstance: any = null;
  private thumbSwiper: any = null;
  private canHoverZoom: boolean = false;
  private isHoverZooming: boolean = false;
  private zoomPos: string = "50% 50%";
  private lightboxZoomed: boolean = false;
  private lightboxPinchStart: number = 0;
  private lightboxPointerMoved: boolean = false;
  private lightboxPanning: boolean = false;
  private lightboxIgnoreClick: boolean = false;
  private lightboxLastTap: number = 0;
  private lightboxPointerStart: { x: number; y: number } = { x: 0, y: 0 };
  private lightboxPan: { x: number; y: number } = { x: 0, y: 0 };
  private lightboxPanFrom: { x: number; y: number; panX: number; panY: number } | null = null;
  private readonly lightboxMoveThreshold: number = 8;
  private readonly lightboxZoomScale: number = 2.15;
  private zoomUrlCache: Record<string, string> = {};
  private thumbOverflowStart: boolean = false;
  private thumbOverflowEnd: boolean = false;

  private slideOptions: object = {
    loop: false,
    preloadImages: false,
    lazy: {
      loadPrevNext: true
    },
    navigation: {
      nextEl: ".productMediaSlider .next",
      prevEl: ".productMediaSlider .prev"
    },
    on: {
      slideChange: (): void => {
        this.syncActiveSlide();
        this.prefetchActiveZoom();
        this.syncHoverZoomFromPointer();
        this.scrollActiveThumbIntoView();
      },
      slideChangeTransitionEnd: (): void => {
        this.syncActiveSlide();
        this.syncHoverZoomFromPointer();
        this.scrollActiveThumbIntoView();
      }
    }
  };

  private thumbOptions: object = {
    slidesPerView: "auto",
    spaceBetween: 8,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    navigation: {
      nextEl: ".productThumbSlider .next",
      prevEl: ".productThumbSlider .prev"
    },
    on: {
      init: (): void => {
        this.updateThumbOverflow();
      },
      slideChange: (): void => {
        this.updateThumbOverflow();
      },
      reachBeginning: (): void => {
        this.updateThumbOverflow();
      },
      reachEnd: (): void => {
        this.updateThumbOverflow();
      },
      resize: (): void => {
        this.updateThumbOverflow();
      }
    }
  };

  private get productMediaGallery(): Array<any> {
    let mediaGallery = (this.productData["media_gallery"] || [])
      .map((galleryItem: any) => {
        if (galleryItem.type === "image") {
          galleryItem.url = galleryItem.url.replace(/\/cache\/[0-9a-f]*/i, "");
          return galleryItem;
        }
        return null;
      })
      .filter(Boolean);

    if (this.addVideo) {
      this.videoEntries.forEach((video, index) => {
        mediaGallery.push({
          position: mediaGallery.length + index + 1,
          type: "video",
          typeVideo: video.typeVideo,
          video: video.video
        });
      });
    }

    return mediaGallery;
  }

  private get videoEntries(): Array<{typeVideo: string; video: string}> {
    const seen: Record<string, true> = {};

    return this.videoSources
      .map((source) => this.extractVideo(source))
      .filter((video): video is {typeVideo: string; video: string} => {
        if (!video?.video) {
          return false;
        }

        const key = `${video.typeVideo}:${video.video}`;
        if (seen[key]) {
          return false;
        }

        seen[key] = true;
        return true;
      });
  }

  private get videoSources(): string[] {
    const raw = this.productData?.swff_video;
    const text = (Array.isArray(raw) ? raw.join(" ") : String(raw || ""))
      .replace(/<[^>]+>/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (!text) {
      return [];
    }

    const urls = text.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?[^.\s,;]*v=[\w-]+|embed\/[\w-]+|shorts\/[\w-]+|live\/[\w-]+)|youtu\.be\/[\w-]+|(?:player\.)?vimeo\.com\/(?:video\/)?\d+)/gi
    );

    if (urls?.length) {
      return urls;
    }

    return text
      .split(/[\n,;]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  private get defaultImageUrl(): string {
    const image = this.productData["default_image"];
    if (typeof image === "string") {
      return image;
    }
    return image ? this.mediaUrl(image, "520x520") : "";
  }

  private get actionLabel(): string {
    return this.productData?.action_label || "";
  }

  private get hasDiscount(): boolean {
    return productHasDiscount(this.productData);
  }

  private get discountPercent(): string {
    return getProductDiscountPercent(this.productData);
  }

  private mediaUrl(image: any, size: string): string {
    const sized = image?.dimensions?.[size];
    if (typeof sized === "string" && sized) {
      return sized;
    }

    if (size === "original") {
      return this.originalMediaUrl(image);
    }

    return this.resizedMediaUrl(image, this.sizeWidth(size));
  }

  private sizeWidth(size: string): number {
    return parseInt(String(size).split("x")[0], 10) || 0;
  }

  private productImagePath(image: any): string {
    let path = image?.url || "";
    if (typeof path !== "string" || !path) {
      return "";
    }

    if (path.startsWith("http")) {
      const absolute = path.match(/\/(?:pub\/)?media\/catalog\/product(\/.+)$/);
      return absolute ? absolute[1] : path;
    }

    path = path.replace(/^\/cdn\/resize\/\d+\/pub\/media\/catalog\/product/, "");
    path = path.replace(/^\/(?:pub\/)?media\/catalog\/product/, "");

    return path.startsWith("/") ? path : `/${path}`;
  }

  private originalMediaUrl(image: any): string {
    const path = this.productImagePath(image);
    if (!path) {
      return "";
    }
    if (path.startsWith("http")) {
      return path;
    }
    return this.withCdn(`/media/catalog/product${path}`);
  }

  private cdnOrigin(): string {
    const cdn = (window as Window & { CDN_URL?: string }).CDN_URL;
    return typeof cdn === "string" ? cdn.replace(/\/$/, "") : "";
  }

  private withCdn(path: string): string {
    if (!path || path.startsWith("http")) {
      return path;
    }

    const cdn = this.cdnOrigin();
    if (!cdn || cdn === window.location.origin) {
      return path;
    }

    return `${cdn}${path.startsWith("/") ? path : `/${path}`}`;
  }

  private resizedMediaUrl(image: any, width: number): string {
    const path = this.productImagePath(image);
    if (!path) {
      return "";
    }
    if (path.startsWith("http") || !width) {
      return this.originalMediaUrl(image);
    }

    return this.withCdn(`/cdn/resize/${width}/pub/media/catalog/product${path}`);
  }

  private displayMediaUrl(image: any): string {
    return this.mediaUrl(image, "520x520");
  }

  private thumbMediaUrl(image: any): string {
    return this.mediaUrl(image, "80x80");
  }

  private zoomMediaUrl(image: any): string {
    const path = this.productImagePath(image);
    if (path && this.zoomUrlCache[path]) {
      return this.zoomUrlCache[path];
    }

    return this.mediaUrl(image, "1600x1600");
  }

  private onSizedImageError(event: Event): void {
    const img = event.currentTarget as HTMLImageElement;
    const fallback = img.dataset.fallback;
    if (!fallback || img.dataset.fallbackUsed === "1") {
      return;
    }

    img.dataset.fallbackUsed = "1";
    img.src = fallback;
  }

  private prefetchActiveZoom(): void {
    const image = this.productMediaGallery[this.activeSlide];
    if (!image || image.type !== "image") {
      return;
    }

    this.prefetchZoomImage(image);
  }

  private prefetchAllZoomImages(): Promise<void[]> {
    return Promise.all(
      this.productMediaGallery
        .filter((image) => image.type === "image")
        .map((image) => this.prefetchZoomImage(image))
    );
  }

  private prefetchZoomImage(image: any): Promise<void> {
    const path = this.productImagePath(image);
    const candidate = this.zoomMediaUrl(image);
    const originalUrl = this.originalMediaUrl(image);
    if (!path) {
      return Promise.resolve();
    }

    if (this.zoomUrlCache[path]) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => {
        this.$set(this.zoomUrlCache, path, candidate);
        resolve();
      };
      probe.onerror = () => {
        this.$set(this.zoomUrlCache, path, originalUrl || candidate);
        resolve();
      };
      probe.src = candidate;
    });
  }

  private zoomStyle(image: any): Record<string, string> {
    return {
      backgroundImage: `url('${this.zoomMediaUrl(image)}')`,
      backgroundPosition: this.zoomPos
    };
  }

  private syncActiveSlide(): void {
    const swiper = this.swiperInstance || (this.$refs.gallerySlider as any)?.$swiper;
    if (swiper) {
      this.activeSlide = swiper.activeIndex;
    }
  }

  private syncHoverZoomFromPointer(): void {
    if (!this.canHoverZoom || !this.$el) {
      this.stopHoverZoom();
      return;
    }

    const image = this.productMediaGallery[this.activeSlide];
    if (!image || image.type !== "image") {
      this.stopHoverZoom();
      return;
    }

    const holder = (this.$el as Element).querySelector(".swiper-slide-active .image__holder") as HTMLElement | null;
    if (holder && holder.matches(":hover")) {
      this.startHoverZoom();
      return;
    }

    this.stopHoverZoom();
  }

  private startHoverZoom(): void {
    if (!this.canHoverZoom) {
      return;
    }

    const image = this.productMediaGallery[this.activeSlide];
    if (!image || image.type !== "image") {
      this.stopHoverZoom();
      return;
    }

    this.prefetchZoomImage(image);
    this.isHoverZooming = true;
  }

  private stopHoverZoom(): void {
    this.isHoverZooming = false;
    this.zoomPos = "50% 50%";
  }

  private onHoverZoom(event: MouseEvent): void {
    if (!this.canHoverZoom) {
      return;
    }

    if (!this.isHoverZooming) {
      this.startHoverZoom();
    }

    if (!this.isHoverZooming) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 100;
    const y = ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 100;
    this.zoomPos = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
  }

  private slideToImage(index: number): void {
    if (this.swiperInstance) {
      this.swiperInstance.slideTo(index);
    }
    this.activeSlide = index;
    this.scrollActiveThumbIntoView();
  }

  private updateThumbOverflow(): void {
    const swiper = this.thumbSwiper || (this.$refs.thumbSlider as any)?.$swiper;
    if (!swiper) {
      this.thumbOverflowStart = false;
      this.thumbOverflowEnd = false;
      return;
    }

    this.thumbOverflowStart = !swiper.isBeginning;
    this.thumbOverflowEnd = !swiper.isEnd;
  }

  private scrollActiveThumbIntoView(): void {
    this.$nextTick(() => {
      const swiper = this.thumbSwiper || (this.$refs.thumbSlider as any)?.$swiper;
      if (swiper && typeof swiper.slideTo === "function") {
        swiper.slideTo(this.activeSlide);
      }
      this.updateThumbOverflow();
    });
  }

  private onThumbClick(_image: any, index: number): void {
    this.slideToImage(index);
  }

  private videoHref(image: any): string {
    if (image.typeVideo === "vimeo") {
      return `https://vimeo.com/${image.video}`;
    }

    return `https://www.youtube.com/watch?v=${image.video}`;
  }

  private videoPoster(image: any): string {
    if (!image?.video) {
      return "";
    }

    if (image.typeVideo === "vimeo") {
      return `https://vumbnail.com/${image.video}.jpg`;
    }

    return `https://i.ytimg.com/vi/${image.video}/hqdefault.jpg`;
  }

  private extractVideo(source: string): {typeVideo: string; video: string} | null {
    const value = source.trim();
    if (!value) {
      return null;
    }

    const youtube = value.match(
      /(?:youtube\.com\/(?:watch\?(?:[^.\s#]*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/i
    );
    if (youtube?.[1]) {
      return { typeVideo: "youtube", video: youtube[1] };
    }

    const vimeo = value.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
    if (vimeo?.[1]) {
      return { typeVideo: "vimeo", video: vimeo[1] };
    }

    if (/^[\w-]{11}$/.test(value)) {
      return { typeVideo: "youtube", video: value };
    }

    if (/^\d{6,}$/.test(value)) {
      return { typeVideo: "vimeo", video: value };
    }

    return null;
  }

  private initializeLightbox(): void {
    if (!this.$el || !(this.$el as Element).querySelector(".lightbox")) {
      return;
    }

    this.lightbox = GLightbox({
      selector: ".product__media a.lightbox",
      touchNavigation: true,
      loop: true,
      zoomable: false,
      draggable: false,
      openEffect: "fade",
      closeEffect: "fade",
      slideEffect: "slide",
      autoplayVideos: true
    });

    this.lightbox.on("open", () => {
      this.stopHoverZoom();
      this.ensureLightboxCounter();
      this.resetLightboxZoom();
      this.bindLightboxZoom();
      this.bindLightboxVideoLoader();
    });

    this.lightbox.on("close", () => {
      this.resetLightboxZoom();
    });

    this.lightbox.on("slide_before_load", () => {
      this.bindLightboxVideoLoader();
    });

    this.lightbox.on("slide_after_load", () => {
      this.bindLightboxZoom();
      this.bindLightboxVideoLoader();
    });

    this.lightbox.on("slide_changed", ((data: { current?: { index?: number; trigger?: Element } }) => {
      const trigger = data?.current?.trigger as HTMLElement | undefined;
      const galleryIndex = trigger?.getAttribute("data-index");
      if (galleryIndex != null) {
        this.slideToImage(Number(galleryIndex));
      }
      this.updateLightboxCounter(data?.current?.index ?? 0);
      this.resetLightboxZoom();
      this.bindLightboxZoom();
      this.bindLightboxVideoLoader();
    }) as () => void);
  }

  private currentLightboxImage(): HTMLImageElement | null {
    return document.querySelector(".glightbox-container .gslide.current .gslide-image img");
  }

  private currentLightboxVideoHost(): HTMLElement | null {
    const slide = document.querySelector(".glightbox-container .gslide.current") as HTMLElement | null;
    if (!slide) {
      return null;
    }

    const isVideo = Boolean(
      slide.querySelector(".gvideo-wrapper, .gvideo-container, .gslide-video")
      || this.productMediaGallery[this.activeSlide]?.type === "video"
    );

    if (!isVideo) {
      return null;
    }

    return (slide.querySelector(".gslide-media") as HTMLElement) || slide;
  }

  private bindLightboxVideoLoader(): void {
    this.$nextTick(() => {
      const host = this.currentLightboxVideoHost();
      if (!host || host.classList.contains("is-video-ready")) {
        return;
      }

      this.ensureVideoLoader(host);
      this.watchVideoIframe(host);
    });
  }

  private ensureVideoLoader(video: HTMLElement): void {
    if (video.querySelector(".avp-video-loader")) {
      return;
    }

    const loader = document.createElement("div");
    loader.className = "avp-video-loader";
    loader.setAttribute("aria-hidden", "true");
    video.appendChild(loader);
  }

  private watchVideoIframe(video: HTMLElement): void {
    const markReady = (): void => {
      video.classList.add("is-video-ready");
    };

    const bindIframe = (iframe: HTMLIFrameElement): void => {
      if (iframe.dataset.avpVideoBound === "1") {
        return;
      }

      iframe.dataset.avpVideoBound = "1";
      iframe.addEventListener("load", markReady, { once: true });
      window.setTimeout(markReady, 12000);
    };

    const existing = video.querySelector("iframe");
    if (existing) {
      bindIframe(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const iframe = video.querySelector("iframe");
      if (iframe) {
        bindIframe(iframe);
        observer.disconnect();
      }
    });

    observer.observe(video, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }

  private bindLightboxZoom(): void {
    this.$nextTick(() => {
      const img = this.currentLightboxImage();
      if (!img || img.dataset.avpZoomBound === "1") {
        return;
      }

      img.dataset.avpZoomBound = "1";
      img.draggable = false;
      img.classList.add("avp-lightbox-zoom");

      img.addEventListener("dragstart", (event: DragEvent) => {
        event.preventDefault();
      });

      img.addEventListener("pointerdown", (event: PointerEvent) => {
        event.preventDefault();
        this.lightboxPointerMoved = false;
        this.lightboxPanning = this.lightboxZoomed;
        this.lightboxPointerStart = { x: event.clientX, y: event.clientY };
        this.lightboxPanFrom = {
          x: event.clientX,
          y: event.clientY,
          panX: this.lightboxPan.x,
          panY: this.lightboxPan.y
        };
        img.classList.toggle("is-panning", this.lightboxPanning);

        if (this.lightboxZoomed) {
          event.stopPropagation();
          img.setPointerCapture(event.pointerId);
        }
      });

      img.addEventListener("pointermove", (event: PointerEvent) => {
        const dx = event.clientX - this.lightboxPointerStart.x;
        const dy = event.clientY - this.lightboxPointerStart.y;
        if (Math.hypot(dx, dy) > this.lightboxMoveThreshold) {
          this.lightboxPointerMoved = true;
        }

        if (!this.lightboxZoomed || !this.lightboxPanning || !this.lightboxPanFrom) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.lightboxPan = this.clampLightboxPan(
          img,
          this.lightboxPanFrom.panX + (event.clientX - this.lightboxPanFrom.x),
          this.lightboxPanFrom.panY + (event.clientY - this.lightboxPanFrom.y)
        );
        this.applyLightboxPan(img);
      });

      img.addEventListener("pointerup", (event: PointerEvent) => {
        if (this.lightboxZoomed) {
          event.stopPropagation();
        }
        this.lightboxPanning = false;
        this.lightboxPanFrom = null;
        img.classList.remove("is-panning");
      });

      img.addEventListener("pointercancel", () => {
        this.lightboxPanning = false;
        this.lightboxPanFrom = null;
        img.classList.remove("is-panning");
      });

      img.addEventListener("click", (event: MouseEvent) => {
        if (this.lightboxPointerMoved || this.lightboxIgnoreClick) {
          this.lightboxIgnoreClick = false;
          return;
        }

        if (this.isCoarsePointer()) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.toggleLightboxZoom(img, event.clientX, event.clientY);
      });

      img.addEventListener("touchend", (event: TouchEvent) => {
        if (event.touches.length > 0 || this.lightboxPointerMoved) {
          return;
        }

        const now = Date.now();
        const tap = event.changedTouches[0];
        if (now - this.lightboxLastTap < 320) {
          this.lightboxIgnoreClick = true;
          this.lightboxLastTap = 0;
          event.preventDefault();
          event.stopPropagation();
          this.toggleLightboxZoom(img, tap.clientX, tap.clientY);
          return;
        }

        this.lightboxLastTap = now;
      });

      img.addEventListener("touchstart", (event: TouchEvent) => {
        if (event.touches.length === 2) {
          this.lightboxPinchStart = this.touchDistance(event);
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (this.lightboxZoomed) {
          event.stopPropagation();
        }
      }, { passive: false });

      img.addEventListener("touchmove", (event: TouchEvent) => {
        if (event.touches.length === 2 && this.lightboxPinchStart) {
          const distance = this.touchDistance(event);
          const ratio = distance / this.lightboxPinchStart;
          const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
          const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;

          if (ratio > 1.12 && !this.lightboxZoomed) {
            this.toggleLightboxZoom(img, midX, midY);
            this.lightboxPinchStart = distance;
          } else if (ratio < 0.88 && this.lightboxZoomed) {
            this.toggleLightboxZoom(img, midX, midY);
            this.lightboxPinchStart = distance;
          }

          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (this.lightboxZoomed && event.touches.length === 1 && this.lightboxPanFrom) {
          this.lightboxPan = this.clampLightboxPan(
            img,
            this.lightboxPanFrom.panX + (event.touches[0].clientX - this.lightboxPanFrom.x),
            this.lightboxPanFrom.panY + (event.touches[0].clientY - this.lightboxPanFrom.y)
          );
          this.applyLightboxPan(img);
          event.preventDefault();
          event.stopPropagation();
        }
      }, { passive: false });
    });
  }

  private isCoarsePointer(): boolean {
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
  }

  private setLightboxZoomedClass(zoomed: boolean): void {
    document.querySelector(".glightbox-container")?.classList.toggle("is-image-zoomed", zoomed);
  }

  private toggleLightboxZoom(img: HTMLImageElement, x: number, y: number): void {
    const slide = img.closest(".gslide") as HTMLElement | null;
    this.lightboxZoomed = !this.lightboxZoomed;
    img.classList.toggle("is-zoomed", this.lightboxZoomed);
    this.setLightboxZoomedClass(this.lightboxZoomed);

    if (slide) {
      slide.classList.toggle("zoomed", this.lightboxZoomed);
    }

    if (this.lightboxZoomed) {
      this.centerLightboxZoomOnPoint(img, x, y);
    } else {
      this.resetLightboxPan(img);
    }
  }

  private resetLightboxZoom(): void {
    this.lightboxZoomed = false;
    this.lightboxPinchStart = 0;
    this.lightboxPanning = false;
    this.lightboxPointerMoved = false;
    this.lightboxPanFrom = null;
    this.setLightboxZoomedClass(false);

    document.querySelectorAll(".glightbox-container .gslide.zoomed").forEach((slide) => {
      slide.classList.remove("zoomed");
    });

    document.querySelectorAll(".glightbox-container img.is-zoomed, .glightbox-container img.avp-lightbox-zoom").forEach((zoomedImg) => {
      zoomedImg.classList.remove("is-zoomed", "is-panning");
      this.resetLightboxPan(zoomedImg as HTMLImageElement);
    });
  }

  private centerLightboxZoomOnPoint(img: HTMLImageElement, clientX: number, clientY: number): void {
    const holder = (img.closest(".gslide-image") as HTMLElement) || img;
    const rect = holder.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    this.lightboxPan = this.clampLightboxPan(
      img,
      -dx * this.lightboxZoomScale,
      -dy * this.lightboxZoomScale
    );
    this.applyLightboxPan(img);
  }

  private clampLightboxPan(img: HTMLImageElement, x: number, y: number): { x: number; y: number } {
    const holder = (img.closest(".gslide-image") as HTMLElement) || img;
    const maxX = (holder.clientWidth * (this.lightboxZoomScale - 1)) / 2;
    const maxY = (holder.clientHeight * (this.lightboxZoomScale - 1)) / 2;
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y))
    };
  }

  private applyLightboxPan(img: HTMLImageElement): void {
    img.style.setProperty("--avp-pan-x", `${this.lightboxPan.x}px`);
    img.style.setProperty("--avp-pan-y", `${this.lightboxPan.y}px`);
  }

  private resetLightboxPan(img: HTMLImageElement): void {
    this.lightboxPan = { x: 0, y: 0 };
    img.style.removeProperty("--avp-pan-x");
    img.style.removeProperty("--avp-pan-y");
  }

  private touchDistance(event: TouchEvent): number {
    const first = event.touches[0];
    const second = event.touches[1];
    return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
  }

  private ensureLightboxCounter(): void {
    const container = document.querySelector(".glightbox-container");
    if (!container || container.querySelector(".gcounter")) {
      return;
    }

    const counter = document.createElement("div");
    counter.className = "gcounter";
    container.appendChild(counter);
    this.updateLightboxCounter(0);
  }

  private updateLightboxCounter(index: number): void {
    const counter = document.querySelector(".glightbox-container .gcounter");
    const total = this.$el
      ? (this.$el as Element).querySelectorAll("a.lightbox").length
      : 0;

    if (counter && total > 0) {
      counter.textContent = `${index + 1} / ${total}`;
    }
  }

  mounted() {
    this.canHoverZoom = window.matchMedia("(hover: hover) and (min-width: 768px)").matches;

    this.$nextTick(async () => {
      this.swiperInstance = (this.$refs.gallerySlider as any)?.$swiper;
      this.thumbSwiper = (this.$refs.thumbSlider as any)?.$swiper;
      await this.prefetchAllZoomImages();
      this.initializeLightbox();
      this.updateThumbOverflow();
    });
  }

  beforeDestroy() {
    if (this.lightbox) {
      this.lightbox.destroy();
    }
  }
}
</script>
